import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useIdleTimer } from "react-idle-timer";
import { addMinutes } from "date-fns";

import { logout } from "@APP/redux";
import { AppLocalStorage } from "@APP/services";

import useAlert from "../useAlert";

interface Props {
  idleTime: number;
}

const IdleTimeout = ({ idleTime }: Props) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const resetRef = useRef<() => void>(() => {});

  const handleLogout = useCallback(() => {
    alert.open(
      "Session Expired",
      "You have not used the application for 10 minutes so we have logged you out",
      [{ text: "Okay", onClick: () => dispatch(logout()) }],
      { persistent: true },
    );
  }, [alert, dispatch]);

  const onIdle = useCallback(() => {
    AppLocalStorage.setItem("auth.sessionAutoLogout", "true");
    handleLogout();
  }, [handleLogout]);

  const onAction = useCallback(() => {
    AppLocalStorage.setItem("auth.sessionIdleTime", addMinutes(new Date(), idleTime).toISOString());
    resetRef.current();
  }, [idleTime]);

  const { reset } = useIdleTimer({
    timeout: 1000 * 60 * idleTime,
    onIdle,
    debounce: 500,
    crossTab: true,
    onAction,
  });

  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  useEffect(() => {
    const autoLogout = AppLocalStorage.getItem("auth.sessionAutoLogout");
    const sessionIdleTime = AppLocalStorage.getItem("auth.sessionIdleTime");

    if (autoLogout || (sessionIdleTime && new Date() > new Date(sessionIdleTime))) {
      handleLogout();
    }
  }, []);

  return <></>;
};

export default IdleTimeout;
